"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { ArrowUpRight } from "lucide-react"

import { Section, SectionHeader } from "@/components/generals/section"

interface BlogPost {
  id: string
  title: string
  slug: string
  imageUrl: string | null
  publishedAt: string | null
}

export default function InsightsSection() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetchPosts() {
      try {
        const res = await fetch("/api/blog?limit=3")
        if (res.ok) {
          const data = await res.json()
          if (!cancelled) setPosts(data.posts || [])
        }
      } catch (error) {
        console.error("Error fetching blog posts:", error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchPosts()
    return () => {
      cancelled = true
    }
  }, [])

  if (!loading && posts.length === 0) return null

  return (
    <Section id="insights" size="md" divider>
      <div className="container">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            title="Notes on building a brand"
            action={
              <Link
                href="/blog"
                className="inline-flex items-center gap-1.5 rounded-sm text-small text-ink-muted transition-colors duration-fast ease-out hover:text-ink"
              >
                All posts <ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            }
          />

          <ul className="mt-10 border-t border-line">
            {loading
              ? [1, 2, 3].map((index) => (
                  <li key={index} className="flex items-center gap-6 border-b border-line py-6">
                    <div className="h-16 w-24 shrink-0 animate-pulse rounded-md bg-surface-2" />
                    <div className="h-4 w-2/3 animate-pulse rounded-sm bg-surface-2" />
                  </li>
                ))
              : posts.map((post) => (
                  <li key={post.id}>
                    <Link
                      href={`/post/${post.slug}`}
                      className="group flex items-center gap-5 border-b border-line py-6 sm:gap-8"
                    >
                      <span className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md border border-line bg-surface-2 sm:h-20 sm:w-32">
                        {post.imageUrl ? (
                          <Image
                            src={post.imageUrl}
                            alt=""
                            fill
                            sizes="128px"
                            className="object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center font-display text-h3 text-ink-subtle">
                            {post.title.charAt(0)}
                          </span>
                        )}
                      </span>

                      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                        {post.publishedAt && (
                          <time
                            dateTime={new Date(post.publishedAt).toISOString()}
                            className="type-eyebrow"
                          >
                            {new Date(post.publishedAt).toLocaleDateString("en-GB", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </time>
                        )}
                        <span className="font-display text-h4 text-balance transition-colors duration-fast ease-out group-hover:text-signal">
                          {post.title}
                        </span>
                      </span>

                      <ArrowUpRight
                        className="size-4 shrink-0 text-ink-subtle transition-colors duration-fast ease-out group-hover:text-ink"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
          </ul>
        </div>
      </div>
    </Section>
  )
}
